import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import { SPRING, sharedAxisX } from "../../../utils/motion";
import styles from "./profilePage.module.css";
import { useAuth } from "../../../context/authContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faTicket,
  faTrophy,
  faGift,
  faGifts,
  faUserGroup,
  faShieldAlt,
  faCreditCard,
  faMoneyBillTransfer,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import {
  faInstagram,
  faFacebook,
  faLinkedin,
} from "@fortawesome/free-brands-svg-icons";
import { OFFICIAL_SOCIAL_LINKS } from "../../../config/socialLinks";
import CambiarContrasena from "./components/cambiarContraseña/cambiarContraseñaPage";
import Datos from "./components/datos/datosPage";
import Recargas from "./components/recargas/recargasPage";
import Desembolsos from "./components/desembolsos/desembolsosPage";
import HistoryPage from "./components/history/historyPage";
import GananciasPage from "./components/ganancias/gananciasPage";
import MisRifasPage from "./components/rifas/misRifasPage";
import MisBonosPage from "./components/bonos/misBonosPage";
import ReferidosPage from "./components/referidos/referidosPage";
import AuthRequiredModal from "../../../components/AuthRequiredModal/AuthRequiredModal";

const SOCIAL_ICONS = {
  instagram: faInstagram,
  facebook: faFacebook,
  linkedin: faLinkedin,
};

/** Redes oficiales (mismo estilo que el footer). */
const ProfileSocialLinks = ({ className }) => (
  <div className={`${styles.socialBlock} ${className || ""}`}>
    <span className={styles.socialLabel}>Síguenos</span>
    <div className={styles.socialList}>
      {OFFICIAL_SOCIAL_LINKS.map((s) => {
        const icon = SOCIAL_ICONS[s.icon_key];
        if (!icon) return null;
        return (
          <a
            key={s.url}
            href={s.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${s.label} de Gold Loterías`}
            title={s.label}
            className={styles.socialLink}
          >
            <FontAwesomeIcon icon={icon} />
          </a>
        );
      })}
    </div>
  </div>
);

const MENU_ITEMS = [
  { key: "datos", icon: faUser, label: "Mis datos" },
  { key: "boletos", icon: faTicket, label: "Mis tickets" },
  { key: "bonos", icon: faGifts, label: "Mis bonos" },
  { key: "ganancias", icon: faTrophy, label: "Mis ganancias" },
  { key: "rifas", icon: faGift, label: "Mis rifas" },
  { key: "referidos", icon: faUserGroup, label: "Referidos" },
  { key: "seguridad", icon: faShieldAlt, label: "Seguridad" },
  { key: "recargas", icon: faCreditCard, label: "Recargas" },
  { key: "retiros", icon: faMoneyBillTransfer, label: "Retiros" },
];

const ProfilePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, isAuthenticated, isLoading, loadProfile } = useAuth();
  // Permite abrir una sección directa: navigate("/perfil", { state: { section: "rifas" } }).
  const requestedSection = location.state?.section;
  const [selectedOption, setSelectedOption] = useState(() =>
    MENU_ITEMS.some((item) => item.key === requestedSection) ? requestedSection : "datos"
  );

  useEffect(() => {
    if (MENU_ITEMS.some((item) => item.key === requestedSection)) {
      setSelectedOption(requestedSection);
    }
    // location.key: re-aplicar aunque se pida la misma sección (p. ej. insignia de bonos).
  }, [requestedSection, location.key]);
  const [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user?.id) {
      loadProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      setShowAuthModal(true);
    }
  }, [isLoading, isAuthenticated]);

  const handleModalClose = () => {
    setShowAuthModal(false);
    navigate("/welcome");
  };

  const selectedIndex = useMemo(
    () =>
      Math.max(
        0,
        MENU_ITEMS.findIndex((item) => item.key === selectedOption)
      ),
    [selectedOption]
  );

  const goToIndex = (index) => {
    const next =
      ((index % MENU_ITEMS.length) + MENU_ITEMS.length) % MENU_ITEMS.length;
    setSelectedOption(MENU_ITEMS[next].key);
  };

  const goPrev = () => goToIndex(selectedIndex - 1);
  const goNext = () => goToIndex(selectedIndex + 1);

  const renderContent = () => {
    switch (selectedOption) {
      case "datos":
        return <Datos />;
      case "boletos":
        return <HistoryPage />;
      case "ganancias":
        return <GananciasPage onGoToRetiros={() => setSelectedOption("retiros")} />;
      case "bonos":
        return <MisBonosPage />;
      case "rifas":
        return <MisRifasPage />;
      case "referidos":
        return <ReferidosPage />;
      case "seguridad":
        return <CambiarContrasena />;
      case "recargas":
        return <Recargas />;
      case "retiros":
        return <Desembolsos onGoToDatos={() => setSelectedOption("datos")} />;
      default:
        return <Datos />;
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Cargando perfil...</p>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return (
      <AuthRequiredModal
        isOpen={showAuthModal}
        onClose={handleModalClose}
        message="Para acceder a tu perfil necesitas iniciar sesion."
      />
    );
  }

  const isVerified = Boolean(
    profile?.is_identity_verified ?? user?.is_identity_verified
  );
  const currentItem = MENU_ITEMS[selectedIndex];

  return (
    <div className={styles.pageWrapper}>
      <aside className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <span className={styles.sidebarTitle}>PERFIL DE USUARIO</span>
        </div>

        <nav className={styles.menuList} aria-label="Secciones del perfil">
          {MENU_ITEMS.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`${styles.menuItem} ${
                selectedOption === item.key ? styles.menuItemActive : ""
              }`}
              onClick={() => setSelectedOption(item.key)}
            >
              {selectedOption === item.key && (
                <motion.span
                  layoutId="profile-menu-indicator"
                  className={styles.menuIndicator}
                  transition={SPRING.bouncy}
                />
              )}
              <FontAwesomeIcon icon={item.icon} className={styles.menuIcon} />
              <span className={styles.menuLabel}>{item.label}</span>
            </button>
          ))}
        </nav>

        <div className={styles.menuMobile}>
          <div className={styles.menuCarousel}>
            <div className={styles.menuCarouselSlide}>
              <button
                type="button"
                className={`${styles.menuNavBtn} ${styles.menuNavPrev}`}
                onClick={goPrev}
                aria-label="Sección anterior"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>

              <div className={styles.menuCarouselCurrent} aria-live="polite">
                <FontAwesomeIcon
                  icon={currentItem.icon}
                  className={styles.menuIcon}
                />
                <span className={styles.menuCarouselLabel}>
                  {currentItem.label}
                </span>
              </div>

              <button
                type="button"
                className={`${styles.menuNavBtn} ${styles.menuNavNext}`}
                onClick={goNext}
                aria-label="Sección siguiente"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>

            <div
              className={styles.menuDots}
              role="tablist"
              aria-label="Ir a sección"
            >
              {MENU_ITEMS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="tab"
                  aria-selected={selectedOption === item.key}
                  aria-label={item.label}
                  className={`${styles.menuDot} ${
                    selectedOption === item.key ? styles.menuDotActive : ""
                  }`}
                  onClick={() => setSelectedOption(item.key)}
                />
              ))}
            </div>
          </div>
        </div>

        <div
          className={`${styles.sidebarStatus} ${
            isVerified
              ? styles.sidebarStatusVerified
              : styles.sidebarStatusPending
          }`}
        >
          <div
            className={`${styles.sidebarStatusIcon} ${
              isVerified ? styles.iconVerified : styles.iconPending
            }`}
            aria-hidden
          >
            {isVerified ? (
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            ) : (
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            )}
          </div>
          <div className={styles.sidebarStatusText}>
            <span className={styles.sidebarStatusTitle}>
              {isVerified
                ? "Identidad verificada"
                : "Verificación pendiente"}
            </span>
            <span className={styles.sidebarStatusHint}>
              {isVerified
                ? "Documento verificado exitosamente."
                : "Sube tu cédula en Mis datos para verificarla."}
            </span>
          </div>
        </div>

        <ProfileSocialLinks className={styles.socialBlockSidebar} />
      </aside>

      <main className={styles.mainContent}>
        <div className={styles.contentBody}>
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={selectedOption}
              variants={sharedAxisX}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {renderContent()}
            </motion.div>
          </AnimatePresence>
        </div>
        <ProfileSocialLinks className={styles.socialBlockMobile} />
      </main>
    </div>
  );
};

export default ProfilePage;
